/**
 * Layout Component
 *
 * Main application layout with navigation and role-based menu items.
 */

import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'
import NotificationCenter from '@/components/NotificationCenter'
import OnboardingWelcome from '@/components/OnboardingWelcome'
import MobileNav from '@/components/mobile/MobileNav'
import { useState, useEffect, useRef } from 'react'

export default function Layout({ children }) {
  const { user, profile, signOut } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const userMenuRef = useRef(null)

  const isActive = (path) => {
    return location.pathname === path
  }

  const handleSignOut = async () => {
    await signOut()
  }

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false)
      }
    }

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isUserMenuOpen])

  const isHomePage = location.pathname === '/'
  const isDeveloperDashboard = location.pathname.startsWith('/dashboard/developer')
  const brandLink = profile?.admin_level
    ? '/dashboard/admin'
    : profile?.role === 'developer'
    ? '/dashboard/developer'
    : '/'

  return (
    <div className="min-h-screen flex flex-col">
      {/* Onboarding Welcome Modal */}
      <OnboardingWelcome />
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isHomePage && !isScrolled
          ? 'bg-transparent border-transparent'
          : 'bg-white/90 backdrop-blur-sm border-b border-secondary-200'
      }`}>
        <nav className="container-custom">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to={brandLink} className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                isHomePage && !isScrolled ? 'bg-white/20' : 'bg-primary-600'
              }`}>
                <span className="text-white font-bold text-lg">B</span>
              </div>
              <span className={`text-xl font-bold transition-colors ${
                isHomePage && !isScrolled ? 'text-white' : 'text-secondary-900'
              }`}>Beaver</span>
            </Link>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-1">
              {user ? (
                <>
                  {/* Buyer Navigation */}
                  {profile?.role === 'buyer' && (
                    <Link
                      to="/dashboard/buyer"
                      className={isActive('/dashboard/buyer') ? 'nav-link-active' : (isHomePage && !isScrolled ? 'px-4 py-2 rounded-lg text-white hover:bg-white/10 transition-colors flex items-center gap-2' : 'nav-link-inactive flex items-center gap-2')}
                    >
                      <svg
                        className={`w-5 h-5 ${isHomePage && !isScrolled ? 'text-white' : 'text-secondary-900'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                        />
                      </svg>
                      My Shortlist
                    </Link>
                  )}

                  {/* Developer Navigation */}
                  {profile?.role === 'developer' && (
                    <Link
                      to="/dashboard/developer"
                      className={isActive('/dashboard/developer') ? 'nav-link-active' : (isHomePage && !isScrolled ? 'px-4 py-2 rounded-lg text-white hover:bg-white/10 transition-colors' : 'nav-link-inactive')}
                    >
                      My Projects
                    </Link>
                  )}

                  {!isDeveloperDashboard && (
                    <Link
                      to="/projects"
                      className={isActive('/projects') ? 'nav-link-active' : (isHomePage && !isScrolled ? 'px-4 py-2 rounded-lg text-white hover:bg-white/10 transition-colors flex items-center gap-2' : 'nav-link-inactive flex items-center gap-2')}
                    >
                      <svg
                        className={`w-5 h-5 ${isHomePage && !isScrolled ? 'text-white' : 'text-secondary-900'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      Find
                    </Link>
                  )}

                  {/* Notification Center */}
                  <NotificationCenter isTransparent={isHomePage && !isScrolled} />

                  {/* User Menu Dropdown */}
                  <div className={`relative ml-auto pl-6 border-l transition-colors ${
                    isHomePage && !isScrolled ? 'border-white/20' : 'border-secondary-200'
                  }`} ref={userMenuRef}>
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                        isHomePage && !isScrolled
                          ? 'hover:bg-white/10'
                          : 'hover:bg-secondary-50'
                      }`}
                    >
                      <svg
                        className={`w-5 h-5 ${
                          isHomePage && !isScrolled ? 'text-white' : 'text-secondary-600'
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </button>

                    {isUserMenuOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-secondary-200 py-1 z-50">
                        {profile?.admin_level && (
                          <>
                            <Link
                              to="/admin"
                              onClick={() => setIsUserMenuOpen(false)}
                              className="block px-4 py-2 text-sm text-primary-700 font-medium hover:bg-primary-50 transition-colors"
                            >
                              Admin Dashboard
                            </Link>
                            <div className="border-t border-secondary-200 my-1"></div>
                          </>
                        )}
                        <Link
                          to="/settings"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors"
                        >
                          Settings
                        </Link>
                        <div className="border-t border-secondary-200 my-1"></div>
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false)
                            handleSignOut()
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-error-600 hover:bg-error-50 transition-colors"
                        >
                          Sign Out
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/projects"
                    className={isActive('/projects') ? 'nav-link-active' : (isHomePage && !isScrolled ? 'px-4 py-2 rounded-lg text-white hover:bg-white/10 transition-colors flex items-center gap-2' : 'nav-link-inactive flex items-center gap-2')}
                  >
                    <svg
                      className={`w-5 h-5 ${isHomePage && !isScrolled ? 'text-white' : 'text-secondary-900'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    Find
                  </Link>
                  <Link
                    to="/for-developers"
                    className={isActive('/for-developers') ? 'nav-link-active' : (isHomePage && !isScrolled ? 'px-4 py-2 rounded-lg text-white hover:bg-white/10 transition-colors' : 'nav-link-inactive')}
                  >
                    For Developers
                  </Link>
                  <div className="flex items-center space-x-2 ml-auto">
                    <Link to="/signin" className={
                      isHomePage && !isScrolled
                        ? 'px-4 py-2 border border-white text-white rounded-lg hover:bg-white/10 transition-colors'
                        : 'btn-outline'
                    }>
                      Sign In
                    </Link>
                    <Link to="/signup" className={
                      isHomePage && !isScrolled
                        ? 'px-4 py-2 bg-white text-primary-700 rounded-lg hover:bg-white/90 transition-colors font-medium'
                        : 'btn-primary'
                    }>
                      Sign Up
                    </Link>
                  </div>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileNavOpen(true)}
                className={`min-h-[44px] min-w-[44px] flex items-center justify-center ${
                  isHomePage && !isScrolled
                    ? 'text-white hover:bg-white/10 rounded-lg transition-colors'
                    : 'text-secondary-600 hover:bg-secondary-50 rounded-lg transition-colors'
                }`}
                aria-label="Open menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className={`flex-1 ${!isHomePage ? 'pt-16' : ''}`}>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-secondary-900 text-secondary-300">
        <div className="container-custom py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">B</span>
                </div>
                <span className="text-xl font-bold text-white">Beaver</span>
              </div>
              <p className="text-sm text-secondary-400 max-w-md">
                Connecting homebuyers with developers for seamless off-plan property sales.
                Discover your dream home before it's built.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/projects" className="hover:text-white transition-colors">
                    Browse Projects
                  </Link>
                </li>
                <li>
                  <Link to="/for-developers" className="hover:text-white transition-colors">
                    For Developers
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="hover:text-white transition-colors">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-white transition-colors">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/privacy" className="hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-white transition-colors">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-secondary-800 mt-8 pt-8 text-sm text-center">
            <p>&copy; {new Date().getFullYear()} Beaver. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Mobile Navigation */}
      <MobileNav
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
        user={user}
        profile={profile}
        signOut={signOut}
      />
    </div>
  )
}
